import { openDB, type DBSchema } from 'idb'

export interface PendingChange {
  id: string
  type: 'create' | 'update' | 'delete' | 'upload'
  entityId?: number
  tempId?: string
  data: unknown
  timestamp: number
  status: 'pending' | 'failed'
  error?: string
}

interface CollectorDB extends DBSchema {
  pendingChanges: {
    key: string
    value: PendingChange
    indexes: { 'by-timestamp': number }
  }
  offlineImages: {
    key: string
    value: { blob: Blob; mimeType: string }
  }
}

const DB_NAME = 'collector-offline'
const DB_VERSION = 1

export async function getDb() {
  return openDB<CollectorDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const changeStore = db.createObjectStore('pendingChanges', { keyPath: 'id' })
      changeStore.createIndex('by-timestamp', 'timestamp')
      db.createObjectStore('offlineImages')
    },
  })
}

export async function addChange(change: PendingChange): Promise<void> {
  const db = await getDb()
  await db.put('pendingChanges', change)
}

export async function getChanges(): Promise<PendingChange[]> {
  const db = await getDb()
  return db.getAllFromIndex('pendingChanges', 'by-timestamp')
}

export async function removeChange(id: string): Promise<void> {
  const db = await getDb()
  await db.delete('pendingChanges', id)
}

export async function clearChanges(): Promise<void> {
  const db = await getDb()
  await db.clear('pendingChanges')
}

export async function saveImage(tempId: string, blob: Blob): Promise<void> {
  const db = await getDb()
  await db.put('offlineImages', { blob, mimeType: blob.type }, tempId)
}

export async function getImage(
  tempId: string
): Promise<{ blob: Blob; mimeType: string } | undefined> {
  const db = await getDb()
  return db.get('offlineImages', tempId)
}

export async function removeImage(tempId: string): Promise<void> {
  const db = await getDb()
  await db.delete('offlineImages', tempId)
}
